import React, { useEffect, useMemo, useRef, useState } from "react";
import isEqual from "lodash/isEqual";

const parseNumber = (value) => {
  if (value === undefined || value === null || value === "") return 0;
  const normalized = String(value).replace(/,/g, "").replace(/\$/g, "").trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isTruthy = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const formatCurrency = (value) => {
  const numeric = parseNumber(value);
  const formatted = Number(Math.abs(numeric).toFixed(2)).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return numeric < 0 ? `-$${formatted}` : `$${formatted}`;
};

const formatAmount = (value) => {
  const num = parseNumber(value);
  if (!num) return "";
  return Number.isInteger(num) ? `${num}` : `${num.toFixed(2)}`;
};

const toMonthLabel = (monthKey = "") => {
  if (typeof monthKey !== "string" || !/^\d{4}-\d{2}$/.test(monthKey)) {
    return "Current Month";
  }

  const [year, month] = monthKey.split("-").map(Number);
  const date = new Date(year, month - 1, 1);
  return date.toLocaleString("en-US", { month: "long", year: "2-digit" }).replace(" ", "-");
};

const toMonthKey = (dateValue) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";

  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}`;
};

const getCurrentMonthKey = (monthKey = "") => {
  if (typeof monthKey === "string" && /^\d{4}-\d{2}$/.test(monthKey)) {
    return monthKey;
  }

  return toMonthKey(new Date());
};

const toYearMonth = (value) => {
  const str = String(value || "").trim();
  const match = /^(\d{4})-(\d{2})/.exec(str);
  return match ? `${match[1]}-${match[2]}` : "";
};

const isMonthWithinCrmCycle = (monthKey, dateFrom, dateTo) => {
  const month = toYearMonth(monthKey);
  if (!month) return true;

  const from = toYearMonth(dateFrom);
  const to = toYearMonth(dateTo);

  if (from && month < from) return false;
  if (to && month > to) return false;
  return true;
};

const normalizeCrmInvoiceRows = (crmData) => {
  if (Array.isArray(crmData)) return crmData;
  if (!crmData || typeof crmData !== "object") return [];

  const rows = [
    {
      title: crmData.title || "CRM Subscription",
      crmSubscription: crmData.crmSubscription,
      crmDateFrom: crmData.crmDateFrom,
      crmDateTo: crmData.crmDateTo,
      isPaid: crmData.isPaid,
      chargeRateDisplay: crmData.chargeRateDisplay,
    },
  ];

  const hasSocialValues = Boolean(
    crmData.socialCrmTitle ||
      crmData.socialCrmSubscription ||
      crmData.socialCrmDateFrom ||
      crmData.socialCrmDateTo
  );

  if (hasSocialValues) {
    rows.push({
      title: crmData.socialCrmTitle || "Social CRM Subscription",
      crmSubscription: crmData.socialCrmSubscription,
      crmDateFrom: crmData.socialCrmDateFrom,
      crmDateTo: crmData.socialCrmDateTo,
      isPaid: false,
    });
  }

  return rows;
};

const normalizeBrandName = (value) => String(value || "").trim();

const dedupeBrands = (values = []) => {
  const seen = new Set();
  const unique = [];

  values.forEach((value) => {
    const normalized = normalizeBrandName(value);
    if (!normalized) return;

    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(normalized);
  });

  return unique;
};

const normalizePostageRows = (rows = []) => {
  if (!Array.isArray(rows)) return [];

  return rows
    .map((row) => {
      const source = row && typeof row === "object" ? row : {};
      return {
        brandName: normalizeBrandName(
          source?.brand_name ||
            source?.brandName ||
            source?.brand ||
            source?.program_brand ||
            source?.programBrand
        ),
        items: parseNumber(source?.items),
        postageUsed: parseNumber(source?.postage_used ?? source?.postageUsed),
      };
    })
    .filter((row) => row.brandName);
};

const buildPostageByBrand = (postageRows = [], brands = []) => {
  const mapByBrand = {};

  brands.forEach((brand) => {
    mapByBrand[brand] = {
      items: 0,
      postageUsed: 0,
    };
  });

  postageRows.forEach((row) => {
    const existingBrand = Object.keys(mapByBrand).find(
      (brand) => brand.toLowerCase() === String(row.brandName || "").toLowerCase()
    );

    const key = existingBrand || row.brandName;
    if (!mapByBrand[key]) {
      mapByBrand[key] = {
        items: 0,
        postageUsed: 0,
      };
    }

    mapByBrand[key] = {
      items: parseNumber(mapByBrand[key]?.items) + parseNumber(row?.items),
      postageUsed:
        parseNumber(mapByBrand[key]?.postageUsed) + parseNumber(row?.postageUsed),
    };
  });

  return mapByBrand;
};

const toLabelFromId = (value = "") =>
  String(value)
    .replace(/^task_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getOverageHours = (task = {}) => {
  const explicitOverage = parseNumber(task?.overageHours);
  if (explicitOverage > 0) return explicitOverage;

  const actual = parseNumber(task?.actualAmHours);
  const included = parseNumber(task?.includedHours);
  const computed = actual - included;
  return computed > 0 ? Number(computed.toFixed(2)) : 0;
};

const InvoiceTab = ({
  monthKey = "",
  projectName = "",
  clientName = "",
  invoiceStatus = "",
  publishFileUrl = "",
  laborData = {},
  technologyData = {},
  fulfilmentData = {},
  miscData = {},
  selectedSectionsByTab = {},
  initialSectionData = {},
  onSectionDataChange,
}) => {
  const invoiceSourceLabel = useMemo(() => {
    const hasApiViaTabs = Array.isArray(initialSectionData?.invoice?.rows);
    const hasApiDirect = Array.isArray(initialSectionData?.rows);

    if (hasApiViaTabs) return "api.tabs.invoice.sections.invoice";
    if (hasApiDirect) return "api.invoice";
    return "fallback.frontend.calculation";
  }, [initialSectionData]);

  const derivedRowsBySection = useMemo(() => {
    const sections = {
      labor: [],
      technology: [],
      fulfilment: [],
      miscellaneous_expenses: [],
    };

    const pushRow = (
      sectionId,
      description,
      {
        chargeRate = null,
        amount = null,
        total = 0,
        chargeRateDisplay = "",
        amountDisplay = "",
      } = {}
    ) => {
      const rowTotal = parseNumber(total);
      const rate = parseNumber(chargeRate);
      const qty = parseNumber(amount);

      sections[sectionId].push({
        section: sectionId,
        description: description || "",
        location: "",
        chargeRate: rate || null,
        amount: qty || null,
        total: Number(rowTotal.toFixed(2)),
        chargeRateDisplay:
          chargeRateDisplay || (rate ? formatCurrency(rate) : ""),
        amountDisplay: amountDisplay || (qty ? formatAmount(qty) : ""),
        totalDisplay: formatCurrency(rowTotal),
      });
    };

    const selectedLabor = new Set(selectedSectionsByTab?.labor || []);
    const selectedTechnology = new Set(selectedSectionsByTab?.technology || []);
    const selectedFulfilment = new Set(selectedSectionsByTab?.fulfilment || []);
    const selectedMisc = new Set(selectedSectionsByTab?.miscellaneous_expenses || []);
    const currentMonthKey = getCurrentMonthKey(monthKey);

    Object.entries(laborData || {}).forEach(([sectionId, task]) => {
      if (!selectedLabor.has(sectionId)) return;
      if (!task || typeof task !== "object") return;

      if (sectionId === "rep_hours_onshore" || sectionId === "rep_hours_offshore") {
        const repSuffix = sectionId === "rep_hours_onshore" ? "Onshore" : "Offshore";
        const resolveRepHours = ({
          numberOfDays,
          numberOfFTEs,
          hoursPerDay,
          chargeManual,
          manualHours,
        }) => {
          if (Boolean(chargeManual)) {
            return parseNumber(manualHours);
          }

          return (
            parseNumber(numberOfDays) *
            parseNumber(numberOfFTEs) *
            parseNumber(hoursPerDay)
          );
        };

        const repRows = [
          {
            title: task?.weekdayRateTitle || "Rep Hours Weekdays",
            includeInInvoice:
              task?.weekdayIncludeInInvoice === undefined
                ? true
                : Boolean(task?.weekdayIncludeInInvoice),
            hours: resolveRepHours({
              numberOfDays: task?.weekdayNumberOfDays,
              numberOfFTEs: task?.weekdayNumberOfFTEs,
              hoursPerDay: task?.weekdayHoursPerDay,
              chargeManual: task?.weekdayChargeManual,
              manualHours: task?.weekdayManualHours,
            }),
            rate: parseNumber(task?.weekdayRate),
          },
          {
            title: task?.weekendRateTitle || "Rep Hours Weekends",
            includeInInvoice:
              task?.weekendIncludeInInvoice === undefined
                ? true
                : Boolean(task?.weekendIncludeInInvoice),
            hours: resolveRepHours({
              numberOfDays: task?.weekendNumberOfDays,
              numberOfFTEs: task?.weekendNumberOfFTEs,
              hoursPerDay: task?.weekendHoursPerDay,
              chargeManual: task?.weekendChargeManual,
              manualHours: task?.weekendManualHours,
            }),
            rate: parseNumber(task?.weekendRate),
          },
          {
            title: task?.socialMediaWeekdayRateTitle || "Social Media Weekdays",
            includeInInvoice:
              task?.socialMediaWeekdayIncludeInInvoice === undefined
                ? true
                : Boolean(task?.socialMediaWeekdayIncludeInInvoice),
            hours: resolveRepHours({
              numberOfDays: task?.socialMediaWeekdayNumberOfDays,
              numberOfFTEs: task?.socialMediaWeekdayNumberOfFTEs,
              hoursPerDay: task?.socialMediaWeekdayHoursPerDay,
              chargeManual: task?.socialMediaWeekdayChargeManual,
              manualHours: task?.socialMediaWeekdayManualHours,
            }),
            rate: parseNumber(task?.socialMediaWeekdayRate),
          },
          {
            title: task?.socialMediaWeekendRateTitle || "Social Media Weekends",
            includeInInvoice:
              task?.socialMediaWeekendIncludeInInvoice === undefined
                ? true
                : Boolean(task?.socialMediaWeekendIncludeInInvoice),
            hours: resolveRepHours({
              numberOfDays: task?.socialMediaWeekendNumberOfDays,
              numberOfFTEs: task?.socialMediaWeekendNumberOfFTEs,
              hoursPerDay: task?.socialMediaWeekendHoursPerDay,
              chargeManual: task?.socialMediaWeekendChargeManual,
              manualHours: task?.socialMediaWeekendManualHours,
            }),
            rate: parseNumber(task?.socialMediaWeekendRate),
          },
          {
            title: task?.overtimeHourRateTitle || "Overtime Charge",
            includeInInvoice:
              task?.overtimeIncludeInInvoice === undefined
                ? true
                : Boolean(task?.overtimeIncludeInInvoice),
            hours: parseNumber(task?.overtimeHours),
            rate: parseNumber(task?.overtimeHourRate),
          },
          {
            title: task?.holidayHourRateTitle || "Holiday Charge",
            includeInInvoice:
              task?.holidayIncludeInInvoice === undefined
                ? true
                : Boolean(task?.holidayIncludeInInvoice),
            hours: parseNumber(task?.holidayHours),
            rate: parseNumber(task?.holidayHourRate),
          },
        ];

        repRows.forEach((item) => {
          if (!item.includeInInvoice) return;
          if (item.hours <= 0 && item.rate <= 0) return;
          const total = item.hours * item.rate;
          pushRow("labor", `${item.title} - ${repSuffix}`, {
            chargeRate: item.rate,
            amount: item.hours,
            total,
          });
        });

        return;
      }

      const taskTitle = task?.title || toLabelFromId(sectionId);
      const isFlatRate =
        task?.isFlatRate === undefined ? true : Boolean(task?.isFlatRate);
      const isTaskSection = String(sectionId || "").startsWith("task_");
      const flatRate = parseNumber(task?.flatRate);
      if (isFlatRate && flatRate > 0) {
        pushRow("labor", taskTitle, {
          chargeRate: flatRate,
          amountDisplay: "",
          total: flatRate,
        });
      }

      if (!isFlatRate && isTaskSection) {
        const hourlyRate = parseNumber(task?.hourlyRate);
        const hours = isTruthy(task?.chargeManual)
          ? parseNumber(task?.manualHours)
          : parseNumber(task?.actualAmHours);

        if (hours > 0 && hourlyRate > 0) {
          pushRow("labor", `${taskTitle} - Hourly`, {
            chargeRate: hourlyRate,
            amount: hours,
            total: hours * hourlyRate,
          });
        }
      }

      const shouldChargeOverage = isFlatRate && Boolean(task?.chargeOverageHours);
      if (shouldChargeOverage) {
        const overageHours = getOverageHours(task);
        const hourlyRate = parseNumber(task?.hourlyRate);

        if (overageHours > 0 && hourlyRate > 0) {
          pushRow("labor", `${taskTitle} - Overage Hours`, {
            chargeRate: hourlyRate,
            amount: overageHours,
            total: overageHours * hourlyRate,
          });
        }
      }
    });

    if (selectedTechnology.has("telecom_support") || selectedTechnology.has("telecom")) {
      const telecomSupport =
        technologyData?.telecom_support || technologyData?.telecom || {};
      const licenseRate = parseNumber(telecomSupport?.licenseRate);
      const licenseCount = parseNumber(
        telecomSupport?.numberOfLicenses ??
          telecomSupport?.noOfLicenses ??
          telecomSupport?.noOfLicense
      );

      if (licenseRate > 0 || licenseCount > 0) {
        pushRow("technology", telecomSupport?.title || "Telecom Licenses", {
          chargeRate: licenseRate,
          amount: licenseCount,
          total: licenseRate * licenseCount,
        });
      }
    }

    if (selectedTechnology.has("telecom_consumption") || selectedTechnology.has("telecom")) {
      const telecomSupport =
        technologyData?.telecom_support || technologyData?.telecom || {};
      const telecomConsumption =
        technologyData?.telecom_consumption || technologyData?.telecom || {};
      const usageRate = parseNumber(
        telecomConsumption?.rate ?? telecomConsumption?.perMinuteRate
      );
      const chargeTimeType = String(telecomConsumption?.chargeTimeType || "handle_time");
      const chargeTimeUnit = String(telecomConsumption?.chargeTimeUnit || "minute");
      const handleTime = parseNumber(
        telecomConsumption?.handleTime ??
          telecomConsumption?.handle_time ??
          telecomConsumption?.acdHandleTime
      );
      const talkTime = parseNumber(
        telecomConsumption?.talkTime ??
          telecomConsumption?.talk_time ??
          telecomConsumption?.acdTalkTime
      );
      const selectedMinutes = chargeTimeType === "talk_time" ? talkTime : handleTime;
      const convertedAmount =
        chargeTimeUnit === "hour" ? selectedMinutes / 60 : selectedMinutes;

      if (usageRate > 0 || selectedMinutes > 0) {
        pushRow("technology", `${telecomSupport?.title || "Telecom"} - Usage`, {
          chargeRate: usageRate,
          amount: convertedAmount,
          amountDisplay:
            chargeTimeUnit === "hour"
              ? `${formatAmount(convertedAmount)} hours`
              : `${formatAmount(convertedAmount)} minutes`,
          total: convertedAmount * usageRate,
          chargeRateDisplay: formatCurrency(usageRate),
        });
      }
    }

    if (selectedTechnology.has("crm_subscription_annual")) {
      const crmRows = normalizeCrmInvoiceRows(
        technologyData?.crm_subscription_annual
      );

      crmRows.forEach((row, rowIndex) => {
        const paidByDisplay =
          String(row?.chargeRateDisplay || "").trim().toLowerCase() === "paid";
        const isPaidRow = isTruthy(row?.isPaid) || paidByDisplay;
        const crmSubscription = parseNumber(row?.crmSubscription);
        const hasAnyValue = Boolean(
          row?.title ||
            row?.crmSubscription ||
            row?.crmDateFrom ||
            row?.crmDateTo
        );

        if (!hasAnyValue) return;
        if (
          !isMonthWithinCrmCycle(
            currentMonthKey,
            row?.crmDateFrom,
            row?.crmDateTo
          )
        ) {
          return;
        }

        if (isPaidRow) {
          pushRow("technology", row?.title || `CRM Subscription ${rowIndex + 1}`, {
            chargeRateDisplay: "Paid",
            amountDisplay: "",
            total: 0,
          });
          return;
        }

        pushRow("technology", row?.title || `CRM Subscription ${rowIndex + 1}`, {
          chargeRate: crmSubscription,
          amount: 1,
          total: crmSubscription,
        });
      });
    }

    if (selectedTechnology.has("misc_telecom")) {
      const miscTelecomRows = Array.isArray(technologyData?.misc_telecom)
        ? technologyData.misc_telecom
        : [];

      miscTelecomRows.forEach((row) => {
        const total = parseNumber(row?.charge);
        if (!row?.heading && !total) return;
        pushRow("technology", row?.heading || "Misc Telecom", {
          chargeRateDisplay: "",
          amountDisplay: "",
          total,
        });
      });
    }

    if (selectedFulfilment.has("fulfillment")) {
      const fulfillmentSection =
        fulfilmentData?.fulfillment && typeof fulfilmentData.fulfillment === "object"
          ? fulfilmentData.fulfillment
          : {};
      const piecesTitle = "Cost of # Pieces ($)";
      const genericPiecesTitle = fulfillmentSection?.title || piecesTitle;
      const postageTitle = fulfillmentSection?.postageTitle || "Postage (at Cost)";
      const chargeBrandwise = isTruthy(fulfillmentSection?.chargeBrandwise);

      const perPieceRate = parseNumber(
        fulfillmentSection?.perPieceRate ??
          fulfillmentSection?.perPieceCampaignRate ??
          fulfillmentSection?.costOfPiecesRate
      );
      const perPieceRateByBrand =
        fulfillmentSection?.perPieceRateByBrand &&
        typeof fulfillmentSection.perPieceRateByBrand === "object" &&
        !Array.isArray(fulfillmentSection.perPieceRateByBrand)
          ? fulfillmentSection.perPieceRateByBrand
          : {};
      const pieceTitleByBrand =
        fulfillmentSection?.pieceTitleByBrand &&
        typeof fulfillmentSection.pieceTitleByBrand === "object" &&
        !Array.isArray(fulfillmentSection.pieceTitleByBrand)
          ? fulfillmentSection.pieceTitleByBrand
          : {};
      const postageTitleByBrand =
        fulfillmentSection?.postageTitleByBrand &&
        typeof fulfillmentSection.postageTitleByBrand === "object" &&
        !Array.isArray(fulfillmentSection.postageTitleByBrand)
          ? fulfillmentSection.postageTitleByBrand
          : {};

      const sectionBrands = dedupeBrands([
        ...(Array.isArray(fulfillmentSection?.program_brands)
          ? fulfillmentSection.program_brands
          : []),
        ...(Array.isArray(fulfillmentSection?.brand_names)
          ? fulfillmentSection.brand_names
          : []),
        ...(Array.isArray(fulfillmentSection?.brands) ? fulfillmentSection.brands : []),
      ]);

      const normalizedPostageRows = normalizePostageRows(
        fulfillmentSection?.postage_rows || fulfillmentSection?.postage_data || []
      );

      const fallbackBrands = dedupeBrands(normalizedPostageRows.map((row) => row.brandName));
      const brands = sectionBrands.length > 0 ? sectionBrands : fallbackBrands;
      const postageByBrand = buildPostageByBrand(normalizedPostageRows, brands);

      if (chargeBrandwise) {
        let hasPiecesRow = false;
        let hasPostageRow = false;

        Object.entries(postageByBrand).forEach(([brandName, values]) => {
          const pieces = parseNumber(values?.items);
          const postAtCost = parseNumber(values?.postageUsed);
          const brandRate = parseNumber(
            perPieceRateByBrand?.[brandName] ?? perPieceRate
          );
          const brandPiecesTitle =
            String(pieceTitleByBrand?.[brandName] || "").trim() ||
            genericPiecesTitle;
          const brandPostageTitle =
            String(postageTitleByBrand?.[brandName] || "").trim() ||
            postageTitle;

          if (pieces > 0 || brandRate > 0) {
            hasPiecesRow = true;
            pushRow("fulfilment", `${brandPiecesTitle} - ${brandName}`, {
              chargeRate: brandRate || null,
              amount: pieces || null,
              total: pieces * brandRate,
            });
          }

          if (postAtCost > 0) {
            hasPostageRow = true;
            pushRow("fulfilment", `${brandPostageTitle} - ${brandName}`, {
              chargeRateDisplay: "",
              amountDisplay: formatAmount(pieces),
              total: postAtCost,
            });
          }
        });

        if (!hasPiecesRow) {
          pushRow("fulfilment", piecesTitle, {
            chargeRate: perPieceRate || null,
            amountDisplay: "",
            total: 0,
          });
        }

        if (!hasPostageRow) {
          pushRow("fulfilment", postageTitle, {
            chargeRateDisplay: "",
            amountDisplay: "",
            total: 0,
          });
        }
      } else {
        const totals = Object.values(postageByBrand).reduce(
          (acc, values) => ({
            pieces: acc.pieces + parseNumber(values?.items),
            postage: acc.postage + parseNumber(values?.postageUsed),
          }),
          { pieces: 0, postage: 0 }
        );

        pushRow("fulfilment", piecesTitle, {
          chargeRate: perPieceRate || null,
          amount: totals.pieces || null,
          total: totals.pieces * perPieceRate,
        });

        if (genericPiecesTitle && genericPiecesTitle !== piecesTitle) {
          const lastRow = sections.fulfilment[sections.fulfilment.length - 1];
          if (lastRow) {
            lastRow.description = genericPiecesTitle;
          }
        }

        pushRow("fulfilment", `${postageTitle} (Program-wise)`, {
          chargeRateDisplay: "",
          amountDisplay: "",
          total: totals.postage,
        });
      }
    }

    if (selectedFulfilment.has("fulfillment_misc")) {
      const miscRows = Array.isArray(fulfilmentData?.fulfillment_misc)
        ? fulfilmentData.fulfillment_misc
        : fulfilmentData?.fulfillment_misc &&
            typeof fulfilmentData.fulfillment_misc === "object"
          ? [fulfilmentData.fulfillment_misc]
          : [];

      miscRows.forEach((row) => {
        const title = String(row?.title || "").trim();
        const rate = parseNumber(row?.rate);
        const items = parseNumber(row?.items);
        if (!title && !rate && !items) return;

        pushRow("fulfilment", title || "Fulfilment Misc", {
          chargeRate: rate || null,
          amount: items || null,
          total: rate * items,
        });
      });
    }

    if (selectedFulfilment.has("fulfillment_tracker")) {
      const tracker =
        fulfilmentData?.fulfillment_tracker &&
        typeof fulfilmentData.fulfillment_tracker === "object"
          ? fulfilmentData.fulfillment_tracker
          : {};
      const currentTrackerMonthValues = tracker?.monthly?.[currentMonthKey] || {};

      const checkTitle = tracker?.checkProcessingTitle || "Check Processing";
      const refundChecksIssuedTitle =
        tracker?.refundChecksIssuedTitle || "Check Amount ($)";
      const checkRate = parseNumber(tracker?.checkProcessingRate);
      const checkCount = parseNumber(currentTrackerMonthValues?.checkCount);
      const checkAmount = parseNumber(currentTrackerMonthValues?.checkAmount);
      const checkTotal = checkCount * checkRate;

      if (checkTitle || checkRate > 0 || checkCount > 0) {
        pushRow("fulfilment", checkTitle, {
          chargeRate: checkRate,
          amount: checkCount || null,
          total: checkTotal,
        });
      }

      if (refundChecksIssuedTitle || checkAmount > 0) {
        pushRow("fulfilment", refundChecksIssuedTitle, {
          chargeRateDisplay: "",
          amountDisplay: "",
          total: checkAmount,
        });
      }

      const voidTitle = tracker?.voidProcessingTitle || "Void";
      const voidRate = parseNumber(tracker?.voidProcessingRate);
      const voidCount = parseNumber(currentTrackerMonthValues?.voidCount);
      const voidAmount = parseNumber(currentTrackerMonthValues?.voidAmount);
      const unsignedVoidTotal =
        voidAmount !== 0 ? Math.abs(voidAmount) : Math.abs(voidCount * voidRate);
      const voidTotal = unsignedVoidTotal === 0 ? 0 : -unsignedVoidTotal;

      if (voidTitle || voidRate > 0 || voidCount > 0 || voidAmount !== 0) {
        pushRow("fulfilment", voidTitle, {
          chargeRateDisplay: "",
          amount: voidCount || null,
          total: voidTotal,
        });
      }

      const stoppedTitle = tracker?.stoppedPaymentTitle || "Stopped Payment";
      const stoppedRate = parseNumber(tracker?.stoppedPaymentRate);
      const stoppedAmount = parseNumber(currentTrackerMonthValues?.stoppedPaymentAmount);
      const stoppedTotal = stoppedAmount > 0 ? stoppedAmount : stoppedRate;

      if (stoppedTitle || stoppedRate > 0 || stoppedAmount > 0) {
        pushRow("fulfilment", stoppedTitle, {
          chargeRateDisplay: "",
          amount: stoppedAmount || null,
          total: stoppedTotal,
        });
      }
    }

    if (selectedFulfilment.has("fulfillment_tracker_1")) {
      const tracker1 =
        fulfilmentData?.fulfillment_tracker_1 &&
        typeof fulfilmentData.fulfillment_tracker_1 === "object"
          ? fulfilmentData.fulfillment_tracker_1
          : {};

      const retrievalRate = parseNumber(
        tracker1?.retrievalRate ?? tracker1?.retrievalHoursRate
      );
      const nonRetrievalRate = parseNumber(tracker1?.nonRetrievalRate);
      const minutesPerRetrieval = parseNumber(tracker1?.minutesPerRetrieval);
      const billRetrievalByHours = isTruthy(
        tracker1?.billRetrievalByHours ?? true
      );
      const retrievalCount = parseNumber(
        tracker1?.monthly?.[currentMonthKey]?.retrievalCount
      );
      const nonRetrievalCount = parseNumber(
        tracker1?.monthly?.[currentMonthKey]?.nonRetrievalCount
      );
      const monthlyHours = (retrievalCount * minutesPerRetrieval) / 60;
      const retrievalMinutes = retrievalCount * minutesPerRetrieval;
      const retrievalBillableAmount = billRetrievalByHours ? monthlyHours : retrievalMinutes;
      const retrievalTotal = retrievalRate * retrievalBillableAmount;

      if (tracker1?.retrievalTitle || retrievalRate > 0 || retrievalBillableAmount > 0) {
        pushRow("fulfilment", tracker1?.retrievalTitle || "Retrieval", {
          chargeRate: retrievalRate,
          amount:
            retrievalBillableAmount > 0
              ? Number(retrievalBillableAmount.toFixed(2))
              : null,
          total: retrievalTotal,
        });
      }

      const nonRetrievalTotal = nonRetrievalRate * nonRetrievalCount;
      const nonRetrievalChargeRateDisplay =
        nonRetrievalCount > 0 || nonRetrievalRate > 0
          ? formatCurrency(nonRetrievalRate)
          : "";

      if (tracker1?.nonRetrievalTitle || nonRetrievalRate > 0 || nonRetrievalCount > 0) {
        pushRow("fulfilment", tracker1?.nonRetrievalTitle || "Non-Retrieval", {
          chargeRateDisplay: nonRetrievalChargeRateDisplay,
          amount: nonRetrievalCount || null,
          total: nonRetrievalTotal,
        });
      }
    }

    if (selectedFulfilment.has("supplies")) {
      const suppliesRows = Array.isArray(fulfilmentData?.supplies)
        ? fulfilmentData.supplies
        : fulfilmentData?.supplies && typeof fulfilmentData.supplies === "object"
          ? [fulfilmentData.supplies]
          : [];

      suppliesRows.forEach((row) => {
        const title = String(row?.title || "").trim();
        const charge = parseNumber(row?.charge ?? row?.miscellaneousSuppliesReceipts);
        if (!title && !charge) return;

        pushRow("fulfilment", title || "Supplies", {
          chargeRate: charge || null,
          amount: 1,
          total: charge,
        });
      });
    }

    if (selectedFulfilment.has("storage")) {
      const storageRows = Array.isArray(fulfilmentData?.storage)
        ? fulfilmentData.storage
        : fulfilmentData?.storage && typeof fulfilmentData.storage === "object"
          ? [fulfilmentData.storage]
          : [];

      storageRows.forEach((row) => {
        const title = String(row?.title || "").trim();
        const charge = parseNumber(row?.charge ?? row?.miscellaneousSuppliesReceipts);
        if (!title && !charge) return;

        pushRow("fulfilment", title || "Storage", {
          chargeRate: charge || null,
          amount: 1,
          total: charge,
        });
      });
    }

    if (selectedFulfilment.has("gift_cards")) {
      const giftCardRows = Array.isArray(fulfilmentData?.gift_cards)
        ? fulfilmentData.gift_cards
        : fulfilmentData?.gift_cards && typeof fulfilmentData.gift_cards === "object"
          ? [fulfilmentData.gift_cards]
          : [];

      giftCardRows.forEach((row) => {
        const title = String(row?.title || "").trim();
        const processingFee = parseNumber(row?.processingFee);
        const numberProcessed = parseNumber(
          row?.numberProcessed ?? row?.numberOfGiftCardsOrdered
        );
        if (!title && !processingFee && !numberProcessed) return;

        pushRow("fulfilment", title || "Gift Cards", {
          chargeRate: processingFee || null,
          amount: numberProcessed || null,
          total: processingFee * numberProcessed,
        });
      });
    }

    if (selectedFulfilment.has("fedex_expenses")) {
      const shipmentRows = Array.isArray(fulfilmentData?.fedex_expenses)
        ? fulfilmentData.fedex_expenses
        : fulfilmentData?.fedex_expenses && typeof fulfilmentData.fedex_expenses === "object"
          ? [fulfilmentData.fedex_expenses]
          : [];

      shipmentRows.forEach((row) => {
        const title = String(row?.title || "").trim();
        const baseCharge = parseNumber(
          row?.baseCharge ?? row?.shipmentCharge ?? row?.charge
        );
        if (!title && !baseCharge) return;

        pushRow("fulfilment", title || "Shipment Charges", {
          chargeRate: baseCharge || null,
          amount: 1,
          total: baseCharge,
        });
      });
    }

    if (selectedMisc.has("professional_voice_recording")) {
      const voice = miscData?.professional_voice_recording || {};
      const total = parseNumber(voice?.quoteAmount);

      if (voice?.title || total) {
        pushRow("miscellaneous_expenses", voice?.title || "Professional Voice Recording", {
          chargeRateDisplay: formatCurrency(total),
          amountDisplay: "",
          total,
        });
      }
    }

    if (selectedMisc.has("misc_telecom")) {
      const miscTelecomRows = Array.isArray(miscData?.misc_telecom)
        ? miscData.misc_telecom
        : [];

      miscTelecomRows.forEach((row) => {
        const total = parseNumber(row?.charge);
        if (!row?.heading && !total) return;
        pushRow("miscellaneous_expenses", row?.heading || "Misc", {
          chargeRateDisplay: formatCurrency(total),
          amountDisplay: "",
          total,
        });
      });
    }

    if (selectedMisc.has("misc_credit")) {
      const miscCreditRows = Array.isArray(miscData?.misc_credit) ? miscData.misc_credit : [];

      miscCreditRows.forEach((row) => {
        const total = parseNumber(row?.charge);
        if (!row?.heading && !total) return;
        pushRow("miscellaneous_expenses", row?.heading || "Misc Credit", {
          chargeRateDisplay: formatCurrency(total),
          amountDisplay: "",
          total,
        });
      });
    }

    return sections;
  }, [fulfilmentData, laborData, miscData, monthKey, selectedSectionsByTab, technologyData]);

  const apiInvoiceRowsBySection = useMemo(() => {
    const invoiceNode =
      initialSectionData?.invoice && typeof initialSectionData.invoice === "object"
        ? initialSectionData.invoice
        : initialSectionData && typeof initialSectionData === "object"
          ? initialSectionData
          : {};

    const sourceRows = Array.isArray(invoiceNode?.rows) ? invoiceNode.rows : [];
    if (!sourceRows.length) return null;

    const grouped = {
      labor: [],
      technology: [],
      fulfilment: [],
      miscellaneous_expenses: [],
    };

    sourceRows.forEach((row) => {
      const sectionId = String(row?.section || "").trim();
      if (!Object.prototype.hasOwnProperty.call(grouped, sectionId)) return;

      const rate = parseNumber(row?.chargeRate);
      const amount = parseNumber(row?.amount);
      const total = parseNumber(row?.total);

      grouped[sectionId].push({
        section: sectionId,
        description: row?.description || "",
        location: row?.location || "",
        chargeRate: rate || null,
        amount: amount || null,
        total: Number(total.toFixed(2)),
        chargeRateDisplay: rate ? formatCurrency(rate) : "",
        amountDisplay: amount ? formatAmount(amount) : "",
        totalDisplay: formatCurrency(total),
      });
    });

    return grouped;
  }, [initialSectionData]);

  const rowsBySection = apiInvoiceRowsBySection || derivedRowsBySection;

  const hasApiRows = Boolean(
    apiInvoiceRowsBySection &&
      Object.values(apiInvoiceRowsBySection).some(
        (rows) => Array.isArray(rows) && rows.length > 0
      )
  );

  const invoiceRenderSource = hasApiRows
    ? invoiceSourceLabel
    : "fallback.frontend.calculation";

  const sectionTotal = (rows = []) =>
    rows.reduce((sum, row) => sum + parseNumber(row?.total), 0);

  const derivedGrandTotal =
    sectionTotal(derivedRowsBySection.labor) +
    sectionTotal(derivedRowsBySection.technology) +
    sectionTotal(derivedRowsBySection.fulfilment) +
    sectionTotal(derivedRowsBySection.miscellaneous_expenses);

  const apiGrandTotal = useMemo(() => {
    const invoiceNode =
      initialSectionData?.invoice && typeof initialSectionData.invoice === "object"
        ? initialSectionData.invoice
        : initialSectionData && typeof initialSectionData === "object"
          ? initialSectionData
          : {};

    if (invoiceNode?.grandTotal === undefined || invoiceNode?.grandTotal === null) {
      return null;
    }

    return parseNumber(invoiceNode.grandTotal);
  }, [initialSectionData]);

  const grandTotal = apiGrandTotal !== null ? apiGrandTotal : derivedGrandTotal;

  const invoicePayload = useMemo(() => {
    const orderedSections = [
      "labor",
      "technology",
      "fulfilment",
      "miscellaneous_expenses",
    ];
    let sortOrder = 1;

    const rows = orderedSections.flatMap((sectionId) =>
      (rowsBySection?.[sectionId] || []).map((row) => {
        const entry = {
          section: row?.section || sectionId,
          description: row?.description || "",
          location: row?.location || "",
          chargeRate: row?.chargeRate,
          amount: row?.amount,
          total: Number(parseNumber(row?.total).toFixed(2)),
          sortOrder,
        };

        sortOrder += 1;
        return entry;
      })
    );

    return {
      invoice: {
        monthKey,
        rows,
        grandTotal: Number(grandTotal.toFixed(2)),
      },
    };
  }, [grandTotal, monthKey, rowsBySection]);

  const [openSections, setOpenSections] = useState({
    labor: true,
    technology: true,
    fulfilment: true,
    miscellaneous_expenses: true,
  });

  const toggleSection = (sectionId) =>
    setOpenSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));

  const lastEmittedPayloadRef = useRef(null);
  const lastLoggedRenderSourceRef = useRef("");

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    if (lastLoggedRenderSourceRef.current === invoiceRenderSource) return;

    lastLoggedRenderSourceRef.current = invoiceRenderSource;
    console.info(`[InvoiceTab][dev] render source: ${invoiceRenderSource}`);
  }, [invoiceRenderSource]);

  useEffect(() => {
    if (typeof onSectionDataChange !== "function") return;
    if (isEqual(lastEmittedPayloadRef.current, invoicePayload)) return;
    if (isEqual(invoicePayload, initialSectionData)) return;

    lastEmittedPayloadRef.current = invoicePayload;
    onSectionDataChange(invoicePayload);
  }, [initialSectionData, invoicePayload, onSectionDataChange]);

  const colHeaderStyle = {
    padding: "10px 16px",
    fontWeight: "700",
    fontSize: "13px",
    color: "#1F3335",
    background: "#E0EEF4",
  };

  const cellPad = { padding: "5px 8px" };

  const inputBox = {
    border: "1px solid #ccc",
    borderRadius: "6px",
    padding: "6px 10px",
    background: "#fff",
    minHeight: "34px",
    fontSize: "13px",
    display: "flex",
    alignItems: "center",
  };

  const highlightedBox = {
    ...inputBox,
    border: "1px solid #FFB319",
    background: "#FFF5E3",
  };

  return (
    <div className="invoice-section-div mb-0">
      <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-2">
        <h3 className="d-flex align-items-center gap-2 mb-0">
          <img src="/assets/images/invoice-user-icon.svg" alt="" />
          <span className="font-16 font-weight-bold heading-color">Invoice Preview</span>
        </h3>
      </div>

      <div className="table-responsive">
        <table className="w-100" style={{ borderCollapse: "collapse" }}>
          {/* Project / Client header */}
          <tbody>
            <tr>
              <td
                colSpan={5}
                style={{
                  background: "#3B607A",
                  color: "#fff",
                  fontWeight: "700",
                  fontSize: "14px",
                  padding: "10px 12px",
                  borderTopLeftRadius: "8px",
                  borderTopRightRadius: "8px",
                }}
              >
                {projectName ? `Project : ${projectName}` : ""}
                {projectName && clientName ? (
                  <span style={{ fontWeight: "400", marginLeft: 6 }}>
                    &lt; {clientName} &gt;
                  </span>
                ) : null}
              </td>
            </tr>
          </tbody>
          {["labor", "technology", "fulfilment", "miscellaneous_expenses"].map((sectionId) => {
            const sectionRows = rowsBySection?.[sectionId] || [];
            if (!sectionRows.length) return null;

            const sectionLabel =
              sectionId === "miscellaneous_expenses"
                ? "Miscellaneous Expenses"
                : toLabelFromId(sectionId);

            const isOpen = openSections[sectionId] !== false;

            return (
              <tbody key={sectionId}>
                {/* Spacer row between sections */}
                <tr style={{ height: 12, lineHeight: 0 }}>
                  <td colSpan={5} style={{ padding: 0, border: "none", background: "transparent", height: 12 }} />
                </tr>
                {/* Section label */}
                <tr>
                  <td
                    colSpan={5}
                    onClick={() => toggleSection(sectionId)}
                    style={{
                      background: "#CFD1D0",
                      fontWeight: "700",
                      fontSize: "14px",
                      padding: "10px 12px",
                      color: "#1F3335",
                      cursor: "pointer",
                      userSelect: "none",
                      borderRadius: isOpen ? "0px" : "12px",
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center" }}>
                      <img
                        src={isOpen ? "/assets/images/accordian-open-icon.svg" : "/assets/images/accordian-close-icon.svg"}
                        alt={isOpen ? "collapse" : "expand"}
                        style={{ width: 16, height: 16, filter: "brightness(0) saturate(100%)", flexShrink: 0 }}
                      />
                      <span style={{ marginLeft: 14 }}>{sectionLabel}</span>
                    </span>
                  </td>
                </tr>

                {/* Animated collapsible content */}
                <tr style={{ height: isOpen ? "auto" : 0 }}>
                  <td colSpan={5} style={{ padding: 0, border: "none", height: isOpen ? "auto" : 0, lineHeight: isOpen ? "normal" : 0 }}>
                    <div
                      style={{
                        maxHeight: isOpen ? "2000px" : "0px",
                        overflow: "hidden",
                        transition: "max-height 0.35s ease",
                      }}
                    >
                      <table className="w-100" style={{ borderCollapse: "collapse" }}>
                        <tbody>
                          {/* Column headers */}
                          <tr>
                            <td style={{ ...colHeaderStyle, minWidth: 150, borderBottomLeftRadius: "8px" }}>Date</td>
                            <td style={{ ...colHeaderStyle, minWidth: 280 }}>Description</td>
                            <td style={{ ...colHeaderStyle, minWidth: 140 }}>Charge Rate</td>
                            <td style={{ ...colHeaderStyle, minWidth: 120 }}>Amount</td>
                            <td style={{ ...colHeaderStyle, minWidth: 140, borderBottomRightRadius: "8px" }}>Total</td>
                          </tr>

                          {/* Data rows */}
                          {sectionRows.map((row, index) => (
                            <tr key={`${sectionId}-${index}`} style={{ background: "#fff" }}>
                              <td style={cellPad}>
                                <div style={inputBox}>{index === 0 ? toMonthLabel(monthKey) : ""}</div>
                              </td>
                              <td style={cellPad}>
                                <div style={inputBox}>{row.description}</div>
                              </td>
                              <td style={cellPad}>
                                <div style={inputBox}>{row.chargeRateDisplay}</div>
                              </td>
                              <td style={cellPad}>
                                <div style={row.amount ? highlightedBox : inputBox}>{row.amountDisplay}</div>
                              </td>
                              <td style={{ ...cellPad, fontWeight: "500", verticalAlign: "middle", fontSize: "13px" }}>
                                {row.totalDisplay}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
                {/* Spacer row after collapsible content */}
                <tr style={{ height: 12, lineHeight: 0 }}>
                  <td colSpan={5} style={{ padding: 0, border: "none", background: "transparent", height: 12 }} />
                </tr>
              </tbody>
            );
          })}

          {/* Grand Total */}
          <tbody>
            <tr>
              <td
                colSpan={4}
                style={{
                  padding: "12px 16px",
                  fontWeight: "700",
                  fontSize: "14px",
                  textAlign: "right",
                  borderTop: "2px solid #dee2e6",
                }}
              >
                Grand Total
              </td>
              <td
                style={{
                  padding: "12px 16px",
                  fontWeight: "700",
                  fontSize: "14px",
                  borderTop: "2px solid #dee2e6",
                }}
              >
                {formatCurrency(grandTotal)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoiceTab;
